import React, { useState } from "react";
import {
  DMModal,
  DMModalContent,
  DMModalFooter,
  DMModalTitle,
} from "../../../shared/DMModal";
import Button from "../../../student/components/generic/button";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useUpdateRenewal } from "./useUpdateRenewal";
import { RenewalTableItem } from "../../utils/quoteUtils";
import { CopyEmailToClipboard } from "./CopyEmailToClipboard";

type FormValues = {
  contacts: { value: string }[];
};

export const EditContactsModal: React.FC<{
  renewal: RenewalTableItem;
  visible: boolean;
  onClose: () => void;
}> = ({ renewal, visible, onClose }) => {
  const { _id: renewalId, contacts = [] } = renewal;

  const updateRenewal = useUpdateRenewal();
  const [isSaving, setIsSaving] = useState(false);

  const { register, control, handleSubmit, getValues } = useForm<FormValues>({
    defaultValues: {
      contacts: contacts.map((contact) => ({ value: contact })),
    },
  });
  const { fields, append, remove } = useFieldArray({
    name: "contacts",
    control,
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsSaving(true);
    try {
      await updateRenewal(
        renewalId,
        "contacts",
        data.contacts.map(({ value }) => value)
      );
      setIsSaving(false);
      onClose();
    } catch (error) {
      setIsSaving(false);
    }
  };

  return (
    <DMModal visible={visible} onClose={onClose}>
      <form
        className="flex w-full flex-col gap-10"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DMModalTitle>Contacts</DMModalTitle>
        <DMModalContent>
          <p className="w-full -translate-y-8 text-center font-bold">
            Editing contacts for {renewal.name}
          </p>
          <ul className="flex w-full flex-col gap-1">
            {fields.length ? (
              fields.map((field, index) => (
                <li key={field.id} className="flex w-full flex-col gap-2">
                  <span className="flex w-full gap-2">
                    <input
                      className="h-9 w-full rounded border px-2 py-0"
                      type="email"
                      {...register(`contacts.${index}.value`, {
                        required: true,
                      })}
                    />
                    <CopyEmailToClipboard
                      getEmail={() => getValues(`contacts.${index}.value`)}
                    />
                    <button
                      className="w-6 text-center text-3xl text-dm-error-500 hover:text-dm-error-600"
                      type="button"
                      title="Remove this contact"
                      onClick={() => remove(index)}
                    >
                      <span className="block -translate-y-[2px]">&times;</span>
                    </button>
                  </span>
                </li>
              ))
            ) : (
              <i className="text-dm-gray-500">No contacts for this customer</i>
            )}
            <li className="mt-1">
              <Button
                className="!py-0"
                type="link"
                submit={false}
                onClick={() => append({ value: "" })}
              >
                + Add contact
              </Button>
            </li>
          </ul>
        </DMModalContent>
        <DMModalFooter>
          <Button onClick={onClose} type="outline" submit={false}>
            Cancel
          </Button>
          <Button isLoading={isSaving}>Save contacts</Button>
        </DMModalFooter>
      </form>
    </DMModal>
  );
};
